import { useEffect, useState } from "react";

export type Appearance = "light" | "dark";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function canMatchMedia() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

function currentAppearance(): Appearance {
  return canMatchMedia() && window.matchMedia(DARK_QUERY).matches
    ? "dark"
    : "light";
}

// Follows the operating system's colour scheme and keeps following it while
// the workspace is open. Falls back to light where matchMedia is missing
// (jsdom, very old browsers).
export function useSystemAppearance(): Appearance {
  const [appearance, setAppearance] = useState<Appearance>(currentAppearance);

  useEffect(() => {
    if (!canMatchMedia()) {
      return;
    }

    const media = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) =>
      setAppearance(event.matches ? "dark" : "light");

    setAppearance(media.matches ? "dark" : "light");

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }

    // Safari before 14 only supports the deprecated listener API.
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  return appearance;
}
